import React, { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus, Search, Download, Lock, Eye, Trash2, ArrowRight, X, Check, Command, Target, Users, BookOpen, AlertTriangle, RefreshCw, BarChart3, Shield
} from 'lucide-react'
import { toast, Toaster } from 'sonner'
import { format } from 'date-fns'

import type {
  Researcher, FullCycle, Cycle, Event, ExportRecord, WorkflowJob,
  CANONICAL_ERROR_TYPES, ErrorType, ActionType, TopicCluster, TelemetryPoint, CurvePoint
} from './lib/types'
import * as api from './lib/api'

// ---------- Prominent Creator Links (required) ----------
function CreatorLinks({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`${compact ? 'text-[11px]' : 'text-sm'} text-ink-light flex items-center gap-3 flex-wrap`}>
      <span className="text-ink-muted">a private instrument by</span>
      <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noopener noreferrer"
         className="hover:text-accent underline underline-offset-2 font-medium">Abhay</a>
      <span className="text-ink-muted">·</span>
      <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noopener noreferrer"
         className="hover:text-accent underline underline-offset-2 font-medium">buy me a coffee</a>
    </div>
  )
}

// ---------- Ambiguity Tolerance Curve (pure SVG, no external libs) ----------
function AmbiguityToleranceCurve({ points }: { points: CurvePoint[] }) {
  if (!points.length) {
    return (
      <div className="card p-8 text-center text-ink-muted">
        Complete a few cycles to see your curve. The line rises as you become more capable of holding complexity without immediate collapse.
      </div>
    )
  }

  const width = 860
  const height = 260
  const pad = { l: 42, r: 16, t: 18, b: 32 }

  const xs = points.map((_, i) => i)
  const maxX = Math.max(1, xs.length - 1)
  const scaleX = (i: number) => pad.l + (i / maxX) * (width - pad.l - pad.r)

  const getY = (v: number | null, min: number, max: number) => {
    if (v == null) return null
    const norm = (v - min) / (max - min)
    return pad.t + (1 - norm) * (height - pad.t - pad.b)
  }

  const hBeforeY = (v: number | null) => getY(v, 0, 10)
  const hAfterY = (v: number | null) => getY(v, 0, 10)
  const systemY = (v: number | null) => getY(v, 0, 10)
  const tolY = (v: number | null) => getY(v, -2, 14)

  const tolPath = points.map((p, i) => {
    const y = tolY(p.tolerance)
    return y == null ? '' : `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${y}`
  }).filter(Boolean).join(' ')

  const hAfterPath = points.map((p, i) => {
    const y = hAfterY(p.h_after)
    return y == null ? '' : `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${y}`
  }).filter(Boolean).join(' ')

  const sysPath = points.map((p, i) => {
    const y = systemY(p.system)
    return y == null ? '' : `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${y}`
  }).filter(Boolean).join(' ')

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} className="max-w-[920px]">
      <defs>
        <linearGradient id="tolGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#2f5d5d" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#2f5d5d" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* grid */}
      <g className="curve-grid">
        {[0,1,2,3,4,5].map(n => {
          const y = pad.t + (n / 5) * (height - pad.t - pad.b)
          return <line key={n} x1={pad.l} x2={width - pad.r} y1={y} y2={y} />
        })}
        {points.map((_, i) => <line key={i} x1={scaleX(i)} x2={scaleX(i)} y1={pad.t} y2={height - pad.b} />)}
      </g>

      {/* axes labels */}
      <text x={pad.l - 6} y={pad.t + 4} fontSize="10" fill="#6b777a" textAnchor="end">high tolerance</text>
      <text x={pad.l - 6} y={height - pad.b - 2} fontSize="10" fill="#6b777a" textAnchor="end">low</text>
      <text x={width - pad.r} y={height - 8} fontSize="10" fill="#6b777a" textAnchor="end">more cycles →</text>

      {/* system recognition (rising = good) */}
      {sysPath && <path d={sysPath} fill="none" className="system-line" />}
      {/* helplessness after (falling = good) */}
      {hAfterPath && <path d={hAfterPath} fill="none" className="helplessness-line" />}

      {/* primary tolerance index (the story) */}
      {tolPath && (
        <>
          <path d={tolPath} fill="none" className="tolerance-line" />
          <path d={`${tolPath} L ${scaleX(points.length-1)} ${height-pad.b} L ${scaleX(0)} ${height-pad.b} Z`} fill="url(#tolGrad)" />
        </>
      )}

      {/* dots */}
      {points.map((p, i) => {
        const x = scaleX(i)
        const ty = tolY(p.tolerance)
        return ty != null ? <circle key={i} cx={x} cy={ty} r="3" fill="#1f2a2e" /> : null
      })}

      <g fontSize="10" fill="#4a5558">
        <text x={pad.l} y={height - 6}>Tolerance index (composite)</text>
        <text x={pad.l + 210} y={height - 6} fill="#8f3d3d">— helplessness after (lower better)</text>
        <text x={pad.l + 430} y={height - 6} fill="#2f5d5d">— system recognition</text>
      </g>
    </svg>
  )
}

// ---------- Main App ----------
export default function Ambit() {
  const [phase, setPhase] = useState<'landing' | 'unlock' | 'app'>('landing')
  const [researcher, setResearcher] = useState<Researcher | null>(null)
  const [sessionToken, setSessionToken] = useState<string>('')

  const [view, setView] = useState<'room' | 'curve' | 'history' | 'signals' | 'settings'>('room')
  const [cycles, setCycles] = useState<Cycle[]>([])
  const [fullCycles, setFullCycles] = useState<Record<string, FullCycle>>({})
  const [events, setEvents] = useState<Event[]>([])
  const [exportsList, setExportsList] = useState<ExportRecord[]>([])

  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')

  // Unlock
  const [passInput, setPassInput] = useState('')
  const [handleInput, setHandleInput] = useState('')

  // Current active cycle (the one you are working on in the room)
  const [activeCycleId, setActiveCycleId] = useState<string | null>(null)
  const [activeFull, setActiveFull] = useState<FullCycle | null>(null)

  // Current cycle local draft state (Predict / Act / Observe / Update)
  const [currentPhase, setCurrentPhase] = useState<'predict' | 'act' | 'observe' | 'update'>('predict')
  const [claimTitle, setClaimTitle] = useState('')
  const [claimSource, setClaimSource] = useState('')
  const [claimExcerpt, setClaimExcerpt] = useState('')

  const [helplessness, setHelplessness] = useState(6)
  const [predicted, setPredicted] = useState('')
  const [assumptions, setAssumptions] = useState<string[]>([])

  const [actionType, setActionType] = useState<ActionType>('primary_source')
  const [actionNote, setActionNote] = useState('')
  const [effort, setEffort] = useState(12)

  const [findings, setFindings] = useState('')
  const [surprise, setSurprise] = useState(4)
  const [sourcesFound, setSourcesFound] = useState(1)

  const [errorTypes, setErrorTypes] = useState<string[]>([])
  const [blame, setBlame] = useState(0) // -2 individual ... +2 systemic
  const [systemFactors, setSystemFactors] = useState('')
  const [learning, setLearning] = useState('')
  const [nextTime, setNextTime] = useState('')

  const [aiLoading, setAiLoading] = useState(false)
  const [aiSuggest, setAiSuggest] = useState<any>(null)

  const [cmdOpen, setCmdOpen] = useState(false)
  const [showNewCycle, setShowNewCycle] = useState(false)

  // Consent
  const [consent, setConsent] = useState(false)

  // Load session
  useEffect(() => {
    const saved = localStorage.getItem('ambit_session')
    if (saved) {
      try {
        const { token, rid, handle, consent: c } = JSON.parse(saved)
        setSessionToken(token)
        setResearcher({ id: rid, handle, telemetry_consent: c ? 1 : 0 })
        setConsent(!!c)
        setPhase('app')
        bootstrapApp(token, rid)
      } catch {}
    }
  }, [])

  // Keyboard
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdOpen(v => !v) }
      if (e.key === 'Escape') setCmdOpen(false)
      if (phase === 'app' && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault(); startNewCycle()
      }
      if (e.key.toLowerCase() === '?' && phase === 'app') { e.preventDefault(); setView('settings') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase])

  async function bootstrapApp(token: string, rid: string) {
    setLoading(true)
    try {
      const [me, cs, exs, evs] = await Promise.all([
        api.getMe(token, rid),
        api.listCycles(token, rid),
        api.listExports(token, rid),
        api.listEvents(token, rid),
      ])
      setResearcher(me)
      setConsent(!!me.telemetry_consent)
      setCycles(cs)
      setExportsList(exs)
      setEvents(evs)

      if (cs.length === 0) {
        await seedDemo(token, rid)
        const fresh = await api.listCycles(token, rid)
        setCycles(fresh)
      }
    } catch (e: any) {
      toast.error('Session expired')
      localStorage.removeItem('ambit_session')
      setPhase('unlock')
    } finally { setLoading(false) }
  }

  async function seedDemo(token: string, rid: string) {
    // Two realistic student news-induced helplessness cycles
    const c1 = await api.createCycle(token, rid, {
      title: 'Viral post: “Campus police just arrested three students for a group chat criticizing the administration”',
      source_hint: 'Instagram + group chat',
      excerpt: 'The post has 47k likes. No link to any official statement or arrest record. Comments are full of “this is why we need to burn it all down”.'
    })
    await api.savePrediction(token, rid, c1.id, {
      helplessness: 8,
      predicted_resolution: 'Probably overblown but the administration will use it to justify more surveillance of student orgs.',
      key_assumptions: ['The post is basically accurate', 'Arrests were for speech alone', 'This is part of a larger pattern of repression']
    })
    await api.saveAction(token, rid, c1.id, {
      action_type: 'primary_source',
      action_note: 'Searched local news + university police blotter + public records request portal. DM’d two people who claimed to be present.',
      effort_minutes: 19
    })
    await api.saveObservation(token, rid, c1.id, {
      findings: 'No record of arrests in the last 10 days matching the description. One person in the chat said it was “a rumor from a rival org”. The original post deleted the image after 3 hours.',
      surprise_level: 7,
      sources_found: 4
    })
    await api.saveUpdate(token, rid, c1.id, {
      error_types: ['single_source', 'catastrophizing', 'binary_framing'],
      blame_vector: 1,
      system_factors: 'Platform amplification of unverified dramatic claims + low trust in official channels makes verification feel like betrayal of the group.',
      learning_extraction: 'A single dramatic claim with no primary evidence can still produce 8/10 helplessness if it confirms a pre-existing narrative of total institutional hostility.',
      next_time: 'Before sharing or spiraling, require at least one primary document or named on-record source within 30 minutes of first encounter.'
    })
    await api.sealCycle(token, rid, c1.id)

    const c2 = await api.createCycle(token, rid, {
      title: '“Gen Z is abandoning the job market entirely because of climate despair” — quoted in a national outlet from a 40-person survey',
      source_hint: 'Reposted from a national paper',
      excerpt: 'The survey was run by a wellness startup. No methodology appendix visible in the article.'
    })
    await api.savePrediction(token, rid, c2.id, {
      helplessness: 5,
      predicted_resolution: 'Probably selection bias in who answers those surveys. Real effect is smaller but real among certain majors.',
      key_assumptions: ['The survey is the main evidence', '“Abandoning” means literally not applying to jobs']
    })
    await api.saveAction(token, rid, c2.id, {
      action_type: 'data_check',
      action_note: 'Found the actual survey press release. Looked up labor force participation + unemployment for 18-24 cohort in BLS + a couple of longitudinal studies on climate anxiety and career plans.',
      effort_minutes: 27
    })
    await api.saveObservation(token, rid, c2.id, {
      findings: 'The 40-person survey was self-selected via Instagram ads. BLS data shows 18-24 LFPR is actually slightly up from 2019 in most quarters. Several peer-reviewed papers show climate concern correlates with *higher* civic and career engagement in certain fields.',
      surprise_level: 6,
      sources_found: 5
    })
    await api.saveUpdate(token, rid, c2.id, {
      error_types: ['availability', 'false_balance', 'omitted_context'],
      blame_vector: -1,
      system_factors: 'Wellness and media incentives reward large-sounding generational narratives. Actual labor data is slower and less viral.',
      learning_extraction: 'A quoted survey from a non-academic source with tiny N is being treated as diagnostic of an entire generation’s economic behavior.',
      next_time: 'For any “generation is doing X” claim, the first move is “what is the actual N and sampling frame?”'
    })
    await api.sealCycle(token, rid, c2.id)

    toast.success('Seeded two realistic student cycles so you can explore the full loop and curve immediately.')
  }

  async function performUnlock() {
    if (!passInput.trim() || !handleInput.trim()) {
      toast.error('Passphrase and handle required')
      return
    }
    setLoading(true)
    try {
      const { researcher: r, token } = await api.unlock(passInput.trim(), handleInput.trim())
      const sess = { token, rid: r.id, handle: r.handle, consent: !!r.telemetry_consent }
      localStorage.setItem('ambit_session', JSON.stringify(sess))
      setSessionToken(token)
      setResearcher(r)
      setConsent(!!r.telemetry_consent)
      setPhase('app')
      setPassInput('')
      await bootstrapApp(token, r.id)
      toast.success(`Room unlocked. Welcome, ${r.handle}.`)
    } catch (e: any) {
      toast.error(e.message || 'Unlock failed')
    } finally { setLoading(false) }
  }

  function lockWorkspace() {
    localStorage.removeItem('ambit_session')
    setPhase('unlock')
    setResearcher(null)
    setSessionToken('')
    setCycles([])
    setActiveCycleId(null)
    setActiveFull(null)
    setView('room')
    toast('Room locked on this device.')
  }

  async function startNewCycle() {
    if (!sessionToken || !researcher) return
    setShowNewCycle(true)
    setClaimTitle('')
    setClaimSource('')
    setClaimExcerpt('')
    setCurrentPhase('predict')
    setHelplessness(6)
    setPredicted('')
    setAssumptions([])
    setActionType('primary_source')
    setActionNote('')
    setFindings('')
    setSurprise(4)
    setErrorTypes([])
    setBlame(0)
    setSystemFactors('')
    setLearning('')
    setNextTime('')
    setAiSuggest(null)
  }

  async function createAndEnterCycle() {
    if (!sessionToken || !researcher || !claimTitle.trim()) return
    setLoading(true)
    try {
      const { id } = await api.createCycle(sessionToken, researcher.id, {
        title: claimTitle.trim(),
        source_hint: claimSource.trim() || undefined,
        excerpt: claimExcerpt.trim() || undefined,
      })
      const fresh = await api.listCycles(sessionToken, researcher.id)
      setCycles(fresh)
      setShowNewCycle(false)
      await loadAndActivate(id)
      setCurrentPhase('predict')
      toast.success('Cycle started. Begin in Predict.')
    } catch (e) { toast.error('Failed to start cycle') } finally { setLoading(false) }
  }

  async function loadAndActivate(id: string) {
    if (!sessionToken || !researcher) return
    setLoading(true)
    try {
      const full = await api.getFullCycle(sessionToken, researcher.id, id)
      setActiveCycleId(id)
      setActiveFull(full)
      // hydrate local state from existing data
      if (full.claim) {
        setClaimTitle(full.claim.title)
        setClaimSource(full.claim.source_hint || '')
        setClaimExcerpt(full.claim.excerpt || '')
      }
      if (full.prediction) {
        setHelplessness(full.prediction.helplessness)
        setPredicted(full.prediction.predicted_resolution)
        setAssumptions(full.prediction.key_assumptions_json ? JSON.parse(full.prediction.key_assumptions_json) : [])
        setCurrentPhase('act')
      }
      if (full.action) {
        setActionType(full.action.action_type as any)
        setActionNote(full.action.action_note)
        setEffort(full.action.effort_minutes || 12)
        setCurrentPhase('observe')
      }
      if (full.observation) {
        setFindings(full.observation.findings)
        setSurprise(full.observation.surprise_level)
        setSourcesFound(full.observation.sources_found || 1)
        setCurrentPhase('update')
      }
      if (full.update) {
        setErrorTypes(full.update.error_types_json ? JSON.parse(full.update.error_types_json) : [])
        setBlame(full.update.blame_vector)
        setSystemFactors(full.update.system_factors)
        setLearning(full.update.learning_extraction)
        setNextTime(full.update.next_time)
        setCurrentPhase('update')
      }
      setView('room')
    } catch (e) { toast.error('Could not load cycle') } finally { setLoading(false) }
  }

  async function savePredict() {
    if (!activeCycleId || !sessionToken || !researcher) return
    await api.savePrediction(sessionToken, researcher.id, activeCycleId, {
      helplessness,
      predicted_resolution: predicted,
      key_assumptions: assumptions.length ? assumptions : undefined,
    })
    await refreshActive()
    setCurrentPhase('act')
  }

  async function saveAct() {
    if (!activeCycleId || !sessionToken || !researcher) return
    await api.saveAction(sessionToken, researcher.id, activeCycleId, {
      action_type: actionType,
      action_note: actionNote,
      effort_minutes: effort,
    })
    await refreshActive()
    setCurrentPhase('observe')
  }

  async function saveObserve() {
    if (!activeCycleId || !sessionToken || !researcher) return
    await api.saveObservation(sessionToken, researcher.id, activeCycleId, {
      findings,
      surprise_level: surprise,
      sources_found: sourcesFound,
    })
    await refreshActive()
    setCurrentPhase('update')
  }

  async function saveUpdateAndSeal() {
    if (!activeCycleId || !sessionToken || !researcher) return
    if (errorTypes.length === 0 || !learning.trim()) {
      toast.error('Select at least one error type and write a learning extraction.')
      return
    }
    await api.saveUpdate(sessionToken, researcher.id, activeCycleId, {
      error_types: errorTypes,
      blame_vector: blame,
      system_factors: systemFactors,
      learning_extraction: learning,
      next_time: nextTime,
    })
    await api.sealCycle(sessionToken, researcher.id, activeCycleId)

    // Consent-based telemetry
    if (consent && researcher.telemetry_consent) {
      const point: TelemetryPoint = {
        topic: (activeFull?.topic_cluster as TopicCluster) || 'other',
        error_types: errorTypes,
        blame_vector: blame,
        system_recognition: Math.max(0, Math.min(10, (systemFactors.length > 40 ? 7 : 4) + (blame > 0 ? 2 : 0))),
        tolerance_delta: (helplessness - 4), // rough: lower after is improvement
        learning_depth: Math.min(1, (learning.length + nextTime.length) / 420),
        sealed_at: Date.now(),
      }
      await api.submitTelemetryConsentPoint(sessionToken, researcher.id, point)
    }

    const fresh = await api.listCycles(sessionToken, researcher.id)
    setCycles(fresh)
    await refreshActive()
    toast.success('Cycle sealed. Your curve just moved.')
    setActiveCycleId(null)
    setActiveFull(null)
    setView('curve')
  }

  async function refreshActive() {
    if (!activeCycleId || !sessionToken || !researcher) return
    const full = await api.getFullCycle(sessionToken, researcher.id, activeCycleId)
    setActiveFull(full)
  }

  async function refreshAll() {
    if (!sessionToken || !researcher) return
    const [cs, exs, evs] = await Promise.all([
      api.listCycles(sessionToken, researcher.id),
      api.listExports(sessionToken, researcher.id),
      api.listEvents(sessionToken, researcher.id),
    ])
    setCycles(cs)
    setExportsList(exs)
    setEvents(evs)
  }

  async function toggleConsent() {
    if (!sessionToken || !researcher) return
    const next = !consent
    await api.setTelemetryConsent(sessionToken, researcher.id, next)
    setConsent(next)
    const me = await api.getMe(sessionToken, researcher.id)
    setResearcher(me)
    const sess = JSON.parse(localStorage.getItem('ambit_session') || '{}')
    sess.consent = next
    localStorage.setItem('ambit_session', JSON.stringify(sess))
    toast(next ? 'Consent recorded. Only anonymized signals will be contributed.' : 'Consent withdrawn. No future signals will be sent.')
  }

  async function requestAISuggestions(phase: 'predict' | 'update') {
    if (!activeCycleId || !sessionToken || !researcher) return
    setAiLoading(true)
    try {
      const ctx = phase === 'predict'
        ? { claim: claimTitle, helplessness, predicted }
        : { findings, predicted, learning }
      const res = await api.getAISuggestions(sessionToken, researcher.id, activeCycleId, phase, ctx)
      setAiSuggest(res.suggestions)
      toast.success('Suggestions loaded. Edit before accepting.')
    } catch (e) {
      toast.error('AI suggestion failed (may not be configured in this deployment)')
    } finally { setAiLoading(false) }
  }

  async function exportCurrent(method: string) {
    if (!activeCycleId || !sessionToken || !researcher) return
    const res: any = await api.exportCycle(sessionToken, researcher.id, activeCycleId, method)
    if (method === 'self_html') {
      // client-side beautiful transcript
      const html = generateCycleTranscriptHTML(activeFull)
      const blob = new Blob([html], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ambit-cycle-${activeCycleId}.html`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } else if (res.url) {
      window.open(res.url, '_blank')
    }
    await refreshAll()
  }

  // Derived
  const visibleCycles = useMemo(() => {
    let list = [...cycles].filter(c => c.status === 'sealed')
    if (search) {
      const s = search.toLowerCase()
      list = list.filter(c => (c.topic_cluster || '').toLowerCase().includes(s))
    }
    return list.sort((a, b) => (b.sealed_at || b.created_at) - (a.sealed_at || a.created_at))
  }, [cycles, search])

  const curvePoints: CurvePoint[] = useMemo(() => {
    return cycles
      .filter(c => c.status === 'sealed' && c.tolerance_index != null)
      .sort((a, b) => (a.sealed_at || 0) - (b.sealed_at || 0))
      .map(c => ({
        ts: c.sealed_at || c.created_at,
        h_before: c.helplessness_before ?? null,
        h_after: c.helplessness_after ?? null,
        system: c.system_recognition ?? null,
        surprise: c.surprise_level ?? null,
        tolerance: c.tolerance_index ?? null,
        cycle_id: c.id,
      }))
  }, [cycles])

  const activeSealed = activeFull && activeFull.status === 'sealed'

  // ---------- RENDER ----------
  if (phase === 'landing') {
    return (
      <div className="room text-ink">
        <div className="max-w-3xl mx-auto px-6 pt-16 pb-24">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-7xl font-semibold tracking-[-3.6px] serif text-ink">ambit</div>
              <div className="mt-1 text-2xl tracking-tight text-ink-muted">sensemaking room</div>
            </div>
            <CreatorLinks />
          </div>

          <div className="mt-10 max-w-2xl text-[15.5px] leading-relaxed text-ink-muted space-y-5">
            <p>News and rumors arrive already weaponized for helplessness. A single post can produce the conviction that everything is collapsing and nothing you do matters.</p>
            <p><span className="text-ink font-medium">ambit</span> is a private instrument for students who want to practice <span className="font-medium">rumor resistance</span> instead of either blind trust or total cynicism.</p>
          </div>

          <div className="mt-9 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            {[
              { title: 'Predict', desc: 'Name your current model and the helplessness it is producing before you go looking for more.' },
              { title: 'Act', desc: 'Choose one bounded, high-leverage move. Not another hour of scrolling.' },
              { title: 'Observe', desc: 'Record what actually came back. Measure the gap between forecast and reality.' },
              { title: 'Update', desc: 'Extract the exact distortion pattern, blame tendency, and structural factors you now see.' },
            ].map((s, i) => (
              <div key={i} className="card p-4">
                <div className="font-medium text-accent">{s.title}</div>
                <div className="text-ink-muted mt-1.5 text-[13.5px]">{s.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-sm text-ink-muted">
            The cumulative record of these loops becomes your <span className="font-medium text-ink">Ambiguity Tolerance Curve</span> — visible evidence that you are getting better at holding complexity without immediate emotional or epistemic collapse.
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => setPhase('unlock')} className="button button-primary px-8 py-3 text-base flex items-center gap-2">
              Enter the room <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => { setPhase('unlock'); setPassInput('demo-student-2026'); setHandleInput('A. Student'); }} className="button px-6 py-3 text-base border">
              Load demo cycles
            </button>
          </div>

          <div className="mt-14 border-t border-[#d8d1c4] pt-6 text-xs text-ink-light max-w-prose">
            Everything stays private by default. The only data that can ever leave is a deliberately exported cycle transcript or fully anonymized, consent-gated cognitive signals (error types, blame shift, system recognition) written to Cloudflare Analytics Engine. No accounts. No social graph. Full erasure always available.
          </div>
          <div className="mt-4"><CreatorLinks /></div>
        </div>
      </div>
    )
  }

  if (phase === 'unlock') {
    return (
      <div className="room min-h-screen flex items-center justify-center p-6 text-ink">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-5xl font-semibold tracking-[-2px] serif">ambit</div>
            <div className="text-ink-muted mt-1">Unlock your private sensemaking room</div>
          </div>

          <div className="card p-7">
            <div className="text-xs uppercase tracking-[1px] text-ink-light mb-1">Your sovereign workspace</div>
            <input className="input w-full mt-4 px-4 py-3 text-base" placeholder="Short handle (e.g. A. Rivera or poli-sci-27)" value={handleInput} onChange={e => setHandleInput(e.target.value)} />
            <input type="password" className="input w-full mt-3 px-4 py-3 text-base font-mono" placeholder="Strong passphrase (never stored)" value={passInput} onChange={e => setPassInput(e.target.value)} />
            <div className="mt-2 text-[11px] text-ink-light">Same passphrase + handle on any device restores everything. The raw secret never leaves this machine after the initial unlock.</div>

            <button onClick={performUnlock} disabled={loading} className="button button-primary w-full mt-6 py-3 text-base">Enter room</button>
            <div className="mt-4 text-[11px] text-ink-light">On another device use the exact same handle and passphrase.</div>
          </div>
          <div className="mt-6 text-center"><CreatorLinks compact /></div>
        </div>
      </div>
    )
  }

  // MAIN APP
  return (
    <div className="room text-ink">
      {/* Top bar */}
      <div className="sticky top-0 z-50 border-b border-[#d8d1c4] bg-[#f7f5f0]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('room'); setActiveCycleId(null); setActiveFull(null) }} className="font-semibold tracking-[-0.8px] serif text-2xl hover:text-accent">ambit</button>
            <div className="text-[10px] px-2 py-px rounded bg-[#f0ede5] text-ink-muted font-mono">{researcher?.handle}</div>
          </div>

          <div className="flex items-center gap-1.5 text-sm">
            <button onClick={() => setView('room')} className={`px-3 py-1.5 rounded ${view === 'room' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}>Room</button>
            <button onClick={() => setView('curve')} className={`px-3 py-1.5 rounded ${view === 'curve' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}>Curve</button>
            <button onClick={() => setView('history')} className={`px-3 py-1.5 rounded ${view === 'history' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}>History</button>
            <button onClick={() => setView('signals')} className={`px-3 py-1.5 rounded flex items-center gap-1 ${view === 'signals' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}><Shield className="w-3.5 h-3.5" /> Signals</button>
            <button onClick={() => setView('settings')} className={`px-3 py-1.5 rounded ${view === 'settings' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}>Privacy</button>

            <button onClick={startNewCycle} className="button button-primary px-4 py-1.5 text-sm flex items-center gap-1.5 ml-2"><Plus className="w-3.5 h-3.5" /> New Cycle</button>
            <button onClick={() => setCmdOpen(true)} className="button px-3 py-1.5 text-sm flex items-center gap-1 border"><Command className="w-3.5 h-3.5" /></button>
            <button onClick={lockWorkspace} className="button px-3 py-1.5 text-sm flex items-center gap-1 border ml-1"><Lock className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 pt-7 pb-24">
        {/* ROOM VIEW — the live loop */}
        {view === 'room' && (
          <>
            {!activeCycleId && (
              <div>
                <div className="uppercase text-xs tracking-[1.5px] text-ink-light mb-1">The loop</div>
                <div className="text-3xl tracking-tight">Predict → Act → Observe → Update</div>
                <div className="mt-3 max-w-prose text-ink-muted">Start a cycle on any claim, post, headline, or rumor that is producing helplessness. Work the stations in order. When you seal the update the cycle is added to your curve.</div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {cycles.filter(c => c.status === 'active').length === 0 && (
                    <div className="col-span-full text-ink-muted border border-dashed rounded p-8 text-center">
                      No active cycle. Start one to enter the room.
                    </div>
                  )}
                  {cycles.filter(c => c.status === 'active').map(c => (
                    <button key={c.id} onClick={() => loadAndActivate(c.id)} className="card p-5 text-left hover:border-accent">
                      <div className="font-medium">{c.topic_cluster || 'Active cycle'}</div>
                      <div className="text-sm text-ink-muted mt-1 line-clamp-2">Started {format(new Date(c.created_at), 'MMM d, HH:mm')}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeCycleId && activeFull && (
              <div className="max-w-4xl">
                <button onClick={() => { setActiveCycleId(null); setActiveFull(null); setView('history') }} className="text-xs text-ink-muted hover:underline mb-3">← back to room</button>

                <div className="text-xl font-medium tracking-tight">{activeFull.claim?.title || claimTitle}</div>
                <div className="text-xs text-ink-light mt-0.5">{activeFull.claim?.source_hint}</div>

                {/* Phase tabs */}
                <div className="flex gap-2 mt-5 flex-wrap">
                  {(['predict','act','observe','update'] as const).map(p => (
                    <button key={p} onClick={() => setCurrentPhase(p)} className={`phase-tab ${currentPhase === p ? 'active' : ''}`}>
                      {p.toUpperCase()}
                    </button>
                  ))}
                </div>

                {/* PREDICT */}
                {currentPhase === 'predict' && (
                  <div className="mt-6 card p-6 station">
                    <div className="station-header mb-3">Predict — before you look for more evidence</div>

                    <div className="text-sm text-ink-muted mb-1">How helpless does this claim make you feel right now? (0 = unaffected, 10 = total collapse)</div>
                    <input type="range" min={0} max={10} value={helplessness} onChange={e => setHelplessness(parseInt(e.target.value))} className="w-full accent-[#2f5d5d]" />
                    <div className="text-2xl font-semibold tabular-nums mt-1">{helplessness}</div>

                    <div className="mt-5 text-sm font-medium">What do you currently believe will turn out to be true?</div>
                    <textarea className="textarea w-full mt-1.5" value={predicted} onChange={e => setPredicted(e.target.value)} placeholder="The administration will use this to expand surveillance..." />

                    <div className="mt-4">
                      <button onClick={() => requestAISuggestions('predict')} disabled={aiLoading} className="text-xs underline text-accent">Ask AI for common distortions this claim type often triggers (optional)</button>
                      {aiSuggest?.predict && <div className="mt-2 text-xs bg-[#f0ede5] p-3 rounded">{JSON.stringify(aiSuggest.predict)}</div>}
                    </div>

                    <div className="mt-5 flex gap-2">
                      <button onClick={savePredict} className="button button-primary px-6">Save prediction &amp; continue to Act</button>
                    </div>
                  </div>
                )}

                {/* ACT */}
                {currentPhase === 'act' && (
                  <div className="mt-6 card p-6 station">
                    <div className="station-header mb-3">Act — one bounded, high-signal move</div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      {(['primary_source','context_search','data_check','conversation','archive','other'] as const).map(t => (
                        <button key={t} onClick={() => setActionType(t)} className={`px-3 py-1.5 rounded border text-left ${actionType === t ? 'bg-ink text-paper border-ink' : 'hover:bg-white'}`}>{t.replace('_', ' ')}</button>
                      ))}
                    </div>

                    <textarea className="textarea w-full mt-3" placeholder="Exactly what you are about to do (keep it small and time-bounded)" value={actionNote} onChange={e => setActionNote(e.target.value)} />
                    <div className="mt-2 flex gap-3 items-center text-sm">
                      <span>Estimated minutes:</span>
                      <input type="number" className="input w-20 px-2 py-1 text-sm" value={effort} onChange={e => setEffort(parseInt(e.target.value) || 10)} />
                    </div>

                    <button onClick={saveAct} className="button button-primary mt-4 px-6">Save action &amp; move to Observe</button>
                  </div>
                )}

                {/* OBSERVE */}
                {currentPhase === 'observe' && (
                  <div className="mt-6 card p-6 station">
                    <div className="station-header mb-3">Observe — what actually came back</div>
                    <textarea className="textarea w-full" placeholder="What you found, which sources, what contradicted your prediction..." value={findings} onChange={e => setFindings(e.target.value)} />

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <div>Surprise level (0 = exactly as predicted, 10 = reality was unrecognizable)</div>
                        <input type="range" min={0} max={10} value={surprise} onChange={e => setSurprise(parseInt(e.target.value))} className="w-full accent-[#2f5d5d] mt-1" />
                        <div className="font-semibold tabular-nums">{surprise}</div>
                      </div>
                      <div>
                        <div>Sources / documents examined</div>
                        <input type="number" className="input mt-1 w-24" value={sourcesFound} onChange={e => setSourcesFound(parseInt(e.target.value) || 0)} />
                      </div>
                    </div>

                    <button onClick={saveObserve} className="button button-primary mt-4 px-6">Save observation &amp; move to Update</button>
                  </div>
                )}

                {/* UPDATE — the harvest */}
                {currentPhase === 'update' && (
                  <div className="mt-6 card p-6 station">
                    <div className="station-header mb-3">Update — extract the pattern (this is the real work)</div>

                    <div className="text-sm font-medium mb-1.5">Error types you recognize in your own thinking on this claim</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(['availability','single_source','catastrophizing','fundamental_attribution','binary_framing','recency','messenger_dismissal','false_balance','omitted_context','platform_distortion'] as const).map(et => {
                        const on = errorTypes.includes(et)
                        return <button key={et} onClick={() => setErrorTypes(on ? errorTypes.filter(x => x !== et) : [...errorTypes, et])} className={`text-xs px-3 py-1 rounded border ${on ? 'bg-ink text-paper border-ink' : 'hover:bg-white'}`}>{et.replace('_', ' ')}</button>
                      })}
                      <input className="input text-xs px-2 py-1 flex-1 min-w-[160px]" placeholder="custom error type" onKeyDown={e => { if (e.key === 'Enter' && e.currentTarget.value) { setErrorTypes([...errorTypes, e.currentTarget.value]); e.currentTarget.value = '' } }} />
                    </div>

                    <div className="mt-5 text-sm font-medium">Blame tendency</div>
                    <div className="flex items-center gap-2 text-xs text-ink-light mt-1">
                      <span>Strongly individual actors</span>
                      <input type="range" min={-2} max={2} step={1} value={blame} onChange={e => setBlame(parseInt(e.target.value))} className="flex-1 accent-[#2f5d5d]" />
                      <span>Strongly systemic / structural</span>
                    </div>
                    <div className="text-[11px] text-ink-light mt-0.5">Moving right = you are seeing more policy, incentives, platform design, history, power relations.</div>

                    <div className="mt-4">
                      <div className="text-sm font-medium mb-1">System / structural factors you can now name</div>
                      <textarea className="textarea w-full h-20" value={systemFactors} onChange={e => setSystemFactors(e.target.value)} placeholder="Platform amplification rewards unverified dramatic claims. Low institutional trust makes verification feel like siding with power..." />
                    </div>

                    <div className="mt-4">
                      <div className="text-sm font-medium mb-1">What this cycle taught you about information environments or your own patterns</div>
                      <textarea className="textarea w-full" value={learning} onChange={e => setLearning(e.target.value)} placeholder="A single dramatic claim with no primary evidence can still produce 8/10 helplessness if it confirms a pre-existing narrative..." />
                    </div>

                    <div className="mt-3">
                      <div className="text-sm font-medium mb-1">Next time I encounter a similar claim I will...</div>
                      <textarea className="textarea w-full h-16" value={nextTime} onChange={e => setNextTime(e.target.value)} />
                    </div>

                    <div className="mt-3">
                      <button onClick={() => requestAISuggestions('update')} disabled={aiLoading} className="text-xs underline text-accent">Get AI-proposed structured tags from your observe + predict (optional)</button>
                      {aiSuggest?.update && <div className="mt-2 text-xs bg-[#f0ede5] p-3 rounded">{JSON.stringify(aiSuggest.update)}</div>}
                    </div>

                    <div className="mt-5 flex gap-3">
                      <button onClick={saveUpdateAndSeal} className="button button-primary px-6">Complete Update &amp; Seal Cycle</button>
                      <button onClick={() => exportCurrent('self_html')} className="button px-4">Export transcript</button>
                    </div>
                    <div className="text-[11px] text-ink-light mt-2">Sealing adds this point to your personal Ambiguity Tolerance Curve. If you have given consent, a fully anonymized signal is contributed to community pattern detection.</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* CURVE VIEW */}
        {view === 'curve' && (
          <div>
            <div className="uppercase text-xs tracking-[1.5px] text-ink-light">Your cumulative record</div>
            <div className="text-3xl tracking-tight mt-1">Ambiguity Tolerance Curve</div>
            <div className="mt-2 max-w-prose text-ink-muted">Each sealed cycle is a point. The composite tolerance index rises when helplessness after the loop is lower than before, when you name more structural factors, and when your learning extraction shows specificity. This is not a wellness score. It is a mirror of your actual sensemaking labor.</div>

            <div className="mt-6 card p-6 overflow-x-auto">
              <AmbiguityToleranceCurve points={curvePoints} />
            </div>

            <div className="mt-4 text-xs text-ink-light">Helplessness-after line falling + system-recognition line rising = movement toward higher capacity to hold unresolved, high-stakes claims without immediate binary collapse.</div>
          </div>
        )}

        {/* HISTORY */}
        {view === 'history' && (
          <div>
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="uppercase text-xs tracking-[1.5px] text-ink-light">Sealed cycles</div>
                <div className="text-3xl tracking-tight">Your rumor resistance log</div>
              </div>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter by topic..." className="input px-3 py-1.5 text-sm w-56" />
            </div>

            {visibleCycles.length === 0 && <div className="text-ink-muted">No sealed cycles yet. Complete a loop in the room.</div>}

            <div className="space-y-3">
              {visibleCycles.map(c => (
                <button key={c.id} onClick={() => loadAndActivate(c.id)} className="card p-5 w-full text-left hover:border-accent group">
                  <div className="font-medium tracking-tight pr-8">{c.topic_cluster || 'Sealed cycle'} <span className="text-xs text-ink-light group-hover:underline ml-2">review →</span></div>
                  <div className="text-sm text-ink-muted mt-1 line-clamp-2">{/* we don't have the claim title here but it's ok */}Sealed {c.sealed_at ? format(new Date(c.sealed_at), 'MMM d, yyyy') : ''}</div>
                  <div className="mt-2 flex gap-2 text-[11px]">
                    {c.error_types_json && JSON.parse(c.error_types_json).slice(0, 3).map((e: string) => <span key={e} className="pill">{e}</span>)}
                    {c.blame_vector != null && <span className="pill">blame {c.blame_vector > 0 ? 'systemic' : c.blame_vector < 0 ? 'individual' : 'mixed'}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* SIGNALS — community needs without identity exposure */}
        {view === 'signals' && (
          <div className="max-w-3xl">
            <div className="flex items-center gap-2"><Shield className="w-5 h-5" /><div className="uppercase text-xs tracking-[1.5px] text-ink-light">Consent-based cognitive telemetry</div></div>
            <div className="text-3xl tracking-tight mt-1">Helping communities see needs without exposing identities</div>

            <div className="mt-5 card p-6 text-sm leading-relaxed">
              When you complete an Update and have the consent toggle on, the app sends a single heavily sanitized data point containing only:
              <ul className="list-disc pl-5 mt-2 space-y-1 text-ink-muted">
                <li>coarse topic cluster (campus, election, labor, conflict, etc.)</li>
                <li>the error types you selected</li>
                <li>your blame vector (-2 individual … +2 systemic)</li>
                <li>a rough system-recognition score</li>
                <li>change in helplessness across the loop</li>
                <li>a learning-depth signal (no text content is ever sent)</li>
              </ul>
              <div className="mt-3">Nothing identifies you. No claim text, no URLs, no handle, no timestamps precise enough to re-identify.</div>
            </div>

            <div className="mt-4 card p-6">
              <div className="font-medium">Your current consent</div>
              <div className="text-sm mt-1 text-ink-muted">Consent is stored only in your private workspace. You can withdraw it at any time.</div>
              <button onClick={toggleConsent} className={`mt-3 button ${consent ? 'border-[#8f3d3d] text-[#8f3d3d]' : 'button-primary'}`}>
                {consent ? 'Withdraw consent (stop contributing signals)' : 'Give consent (contribute anonymized signals)'}
              </button>
            </div>

            <div className="mt-6 text-xs text-ink-light">
              Example of what a signals dashboard (student government, counseling center, mutual aid group) can see from aggregated Analytics Engine data: “In the last 14 days, the dominant error pattern on campus-related claims was single-source + catastrophizing. Average movement toward systemic attribution after a full loop: +1.1.”
              This is the point of the instrument: communities can see where sensemaking is currently breaking without ever being able to point at any individual.
            </div>
          </div>
        )}

        {/* PRIVACY / SETTINGS */}
        {view === 'settings' && researcher && (
          <div className="max-w-2xl">
            <div className="text-2xl tracking-tight mb-6">Privacy &amp; sovereignty</div>

            <div className="card p-6">
              <div className="font-medium">Your workspace key</div>
              <div className="text-sm text-ink-muted mt-1">Handle: <span className="font-medium">{researcher.handle}</span>. The passphrase you used is the only way back in on other devices. It is never stored on the server.</div>
              <button onClick={lockWorkspace} className="mt-4 button px-4 py-1.5 text-sm">Lock room on this device</button>
            </div>

            <div className="card p-6 mt-4">
              <div className="font-medium">Consent for community signals</div>
              <div className="text-sm text-ink-muted mt-1">Controls whether completed cycles contribute the tiny anonymized cognitive point described in the Signals view.</div>
              <button onClick={toggleConsent} className="mt-3 button px-4 py-1.5 text-sm">{consent ? 'Consent ON — click to withdraw' : 'Consent OFF — click to enable'}</button>
            </div>

            <div className="card p-6 mt-4">
              <div className="font-medium">Full data archive</div>
              <div className="text-sm text-ink-muted mt-1">Download a portable JSON of every cycle, claim, prediction, action, observation, and update.</div>
              <button onClick={() => api.downloadFullArchive(sessionToken, researcher.id)} className="mt-3 button px-4 py-1.5 text-sm flex items-center gap-2"><Download className="w-3.5 h-3.5" /> Download my archive</button>
            </div>

            <div className="card p-6 mt-4 border-l-4 border-[#8f3d3d]">
              <div className="font-medium text-[#8f3d3d]">Complete erasure</div>
              <div className="text-sm mt-2">Deletes every cycle, claim, prediction, action, observation, update, AI suggestion log, and all R2 exports. Irreversible.</div>
              <button onClick={async () => {
                const phrase = `I accept that in five years I may regret deleting the record of how I learned to resist helplessness.`
                const conf = prompt(`Type exactly:\n${phrase}`)
                if (conf !== phrase) { toast.error('Phrase did not match'); return }
                await api.triggerErasure(sessionToken, researcher.id, conf)
                localStorage.removeItem('ambit_session')
                setPhase('landing')
              }} className="mt-4 text-sm px-4 py-1.5 border border-[#8f3d3d] text-[#8f3d3d] rounded flex items-center gap-2 hover:bg-[#8f3d3d] hover:text-white"><Trash2 className="w-3.5 h-3.5" /> Erase entire room</button>
            </div>

            <div className="mt-8 text-xs text-ink-light">This instrument exists so students can do rigorous, private epistemic work on the exact claims that are currently breaking them. The only thing that ever leaves is what you deliberately export or what you explicitly consent to contribute as an anonymized signal.</div>
            <div className="mt-3"><CreatorLinks compact /></div>
          </div>
        )}
      </div>

      {/* New cycle modal */}
      <AnimatePresence>
        {showNewCycle && (
          <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" onClick={() => setShowNewCycle(false)}>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="bg-white w-full max-w-xl rounded-lg border p-7" onClick={e => e.stopPropagation()}>
              <div className="font-medium text-xl tracking-tight">Start a new cycle</div>
              <div className="text-sm text-ink-muted mt-1">Paste or describe the claim, post, headline, or rumor that is producing helplessness.</div>

              <input className="input w-full mt-4 px-4 py-2.5" placeholder="Claim / headline / post summary" value={claimTitle} onChange={e => setClaimTitle(e.target.value)} />
              <input className="input w-full mt-2 px-4 py-2.5 text-sm" placeholder="Source hint (Instagram, group chat, The Times, professor, etc.)" value={claimSource} onChange={e => setClaimSource(e.target.value)} />
              <textarea className="textarea w-full mt-2 h-20 p-3 text-sm" placeholder="Key excerpt or description (optional but useful)" value={claimExcerpt} onChange={e => setClaimExcerpt(e.target.value)} />

              <div className="mt-4 flex gap-3">
                <button onClick={createAndEnterCycle} disabled={!claimTitle.trim()} className="button button-primary flex-1 py-2.5">Create cycle &amp; enter Predict</button>
                <button onClick={() => setShowNewCycle(false)} className="button flex-1 py-2.5">Cancel</button>
              </div>
              <div className="text-[11px] text-ink-light mt-3">You can edit details later. The point is to capture the claim while the affective charge is still fresh.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Command palette */}
      <AnimatePresence>
        {cmdOpen && (
          <div className="cmdk" onClick={() => setCmdOpen(false)}>
            <div className="cmdk-inner" onClick={e => e.stopPropagation()}>
              <div className="p-3 text-sm border-b">Command palette</div>
              <div className="p-2 text-sm">
                {[
                  { label: 'New cycle', action: () => { setCmdOpen(false); startNewCycle() } },
                  { label: 'Go to Curve', action: () => { setCmdOpen(false); setView('curve') } },
                  { label: 'Go to History', action: () => { setCmdOpen(false); setView('history') } },
                  { label: 'Signals & consent', action: () => { setCmdOpen(false); setView('signals') } },
                  { label: 'Privacy center', action: () => { setCmdOpen(false); setView('settings') } },
                  { label: 'Lock room', action: () => { setCmdOpen(false); lockWorkspace() } },
                ].map((c, i) => (
                  <div key={i} onClick={c.action} className="px-4 py-2 hover:bg-[#f7f5f0] cursor-pointer rounded flex justify-between items-center">{c.label}<ArrowRight className="w-3.5 h-3.5 text-ink-light" /></div>
                ))}
              </div>
              <div className="p-3 text-[10px] text-ink-light border-t">⌘K • ESC to close • ⌘N new cycle</div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <Toaster position="top-center" richColors closeButton />
    </div>
  )

  // Self-contained beautiful cycle transcript (client-side export)
  function generateCycleTranscriptHTML(full: FullCycle | null) {
    if (!full) return '<html><body>No data</body></html>'
    const date = new Date().toLocaleDateString()
    const u = full.update
    const errs = u ? JSON.parse(u.error_types_json || '[]').join(', ') : ''
    return `<!doctype html>
<html><head><meta charset="utf-8"><title>ambit — ${full.claim?.title || 'cycle'}</title>
<style>body{font-family:system-ui,-apple-system,serif;margin:40px auto;max-width:720px;line-height:1.65;color:#1f2a2e;background:#f7f5f0;padding:20px} .meta{font-size:12px;color:#5c6b6d} h1{font-size:26px;letter-spacing:-0.6px} .section{margin:28px 0} .label{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#2f5d5d;margin-bottom:4px} .small{font-size:12px;color:#5c6b6d}</style>
</head><body>
<h1>${full.claim?.title || 'Sealed cycle'}</h1>
<div class="meta">ambit sensemaking room • sealed ${date} • private instrument</div>

<div class="section"><div class="label">Claim / rumor encountered</div><p>${full.claim?.excerpt || full.claim?.title}</p></div>

<div class="section"><div class="label">Predict</div>
<p>Helplessness before: <strong>${full.prediction?.helplessness ?? '—'}</strong></p>
<p>${full.prediction?.predicted_resolution || ''}</p>
</div>

<div class="section"><div class="label">Act</div>
<p>${full.action?.action_note || ''} (${full.action?.effort_minutes || '?'} min)</p>
</div>

<div class="section"><div class="label">Observe</div>
<p>${full.observation?.findings || ''}</p>
<p>Surprise: ${full.observation?.surprise_level ?? '—'}</p>
</div>

<div class="section"><div class="label">Update — the harvest</div>
<p><strong>Error types:</strong> ${errs}</p>
<p><strong>Blame vector:</strong> ${full.update?.blame_vector ?? '—'} (−2 individual → +2 systemic)</p>
<p><strong>System factors named:</strong> ${full.update?.system_factors || ''}</p>
<p><strong>Learning extraction:</strong> ${full.update?.learning_extraction || ''}</p>
<p><strong>Next time:</strong> ${full.update?.next_time || ''}</p>
</div>

<div class="small">This transcript was generated from a private, sovereign workspace. No platform was involved in its creation or distribution. The only data that ever leaves is what the student deliberately exports.</div>
</body></html>`
  }
}
